/**
 * SA-6 — commission estimator rates (config-driven; see migration
 * 2026-06-18-007-commission-estimator-rates). Rates are editable in the DB without a deploy.
 */
import { supabase } from "./supabase";
import type { CommissionEstimatorRate } from "../utils/commissionEstimator";

export async function getCommissionEstimatorRates(): Promise<
  CommissionEstimatorRate[]
> {
  const { data, error } = await supabase
    .from("commission_estimator_rates")
    .select("rate_key, label, amount, category")
    .eq("is_active", true)
    .order("sort_order");
  if (error) {
    throw error;
  }
  return (data ?? []).map((r) => ({
    rate_key: r.rate_key as string,
    label: r.label as string,
    amount: Number(r.amount),
    category: r.category as CommissionEstimatorRate["category"],
  }));
}

/**
 * Public (anon-safe) rate read for the standalone /commission-estimator-public tool. Calls the bounded
 * SECURITY DEFINER RPC get_public_commission_estimator_rates (migration 2026-07-03-002), which returns
 * ONLY rate_key/label/amount/category for active rates — anon never touches the table. Single source of
 * truth (reads commission_estimator_rates), no synced copy.
 */
export async function getPublicCommissionEstimatorRates(): Promise<
  CommissionEstimatorRate[]
> {
  // New RPC not yet in the generated DB types — cast the fn name; the row shape is asserted below.
  const { data, error } = await supabase.rpc(
    "get_public_commission_estimator_rates" as never,
  );
  if (error) {
    throw error;
  }
  const rows = (data ?? []) as Array<{
    rate_key: string;
    label: string;
    amount: number | string;
    category: string;
  }>;
  return rows.map((r) => ({
    rate_key: r.rate_key,
    label: r.label,
    amount: Number(r.amount),
    category: r.category as CommissionEstimatorRate["category"],
  }));
}
